import { breakpoints } from "@/app/settings";
import { RegionHelper } from "@/common/region_helper";
import CookiesNotice from "@/components/cookies-notice/CookiesNotice.vue";
import CurrencyFormatter from "@/components/CurrencyFormatter.vue";
import ContactForm from "@/components/forms/ContactForm.vue";
import GenericPaymentForm from "@/components/forms/GenericPaymentForm.vue";
import LeadCaptureForm from "@/components/forms/LeadCaptureForm.vue";
import ProductTrialDialog from "@/components/forms/product-trial/ProductTrialDialog.vue";
import ProductTrialForm from "@/components/forms/product-trial/ProductTrialForm.vue";
import ProductTrialNavbarButton from "@/components/forms/product-trial/ProductTrialNavbarButton.vue";
import SearchForm from "@/components/forms/SearchForm.vue";
import { contactCaptureComponents } from "@/components/forms/utils";
import LoginGatekeeper from "@/components/login-gatekeeper/LoginGatekeeper.vue";
import { useDialogs } from "@/components/UI/dialog/useDialog";
import EventsList from "@/events/EventsList.vue";
import { DropdownInstance, useDropdown } from "@/hooks/useDropdown";
import { useToggle, useWindowScroll, useWindowSize } from "@vueuse/core";
import {
  computed,
  defineComponent,
  onMounted,
  ref,
  toRef,
  watch,
  watchEffect,
} from "vue";

/** Latest action triggered by the user. */
export const latestAction = ref<string | null>(null);

export const App = defineComponent({
  compilerOptions: {
    delimiters: ["[[", "]]"],
  },
  components: {
    ContactForm,
    CookiesNotice,
    CurrencyFormatter,
    GenericPaymentForm,
    LeadCaptureForm,
    LoginGatekeeper,
    ProductTrialDialog,
    ProductTrialNavbarButton,
    ProductTrialForm,
    SearchForm,
    EventsList,
    ...contactCaptureComponents,
  },
  setup() {
    const triggerAction = (actionId: string) => (latestAction.value = actionId);
    const { show: showDialog, hide: hideDialog } = useDialogs();

    const navbar = useNavbar();
    const dropdowns = useDropdowns();
    const scrollbar = useScrollNavbar();

    return {
      scrollbar,
      navbar,
      dropdowns,
      region: RegionHelper,
      triggerAction,
      showDialog,
      hideDialog,
    };
  },
});

function useNavbar() {
  const [expanded, toggleExpand] = useToggle();
  const { width } = useWindowSize();

  const isMobile = computed<boolean>(() => width.value < breakpoints.lg);

  watch(
    isMobile,
    (value) => {
      if (!value) {
        toggleExpand(false);
      }
    },
    { immediate: true }
  );

  return { isMobile, expanded, toggleExpand };
}

function useScrollNavbar() {
  const scrollbar = ref<HTMLElement>();
  const hidden = ref<boolean>(true);
  const { y } = useWindowScroll();
  const { width } = useWindowSize();
  const isMobile = computed<boolean>(() => width.value < breakpoints.lg);

  const scrollOffset = 5;

  watchEffect(() => {
    if (!scrollbar.value) {
      return;
    }

    // Hide on mobile, or hide on desktop when scroll is < 40
    if (
      (isMobile.value && !hidden.value) ||
      (!isMobile.value && y.value < scrollOffset && !hidden.value)
    ) {
      hidden.value = true;
      scrollbar.value.classList.add("d-none");
    } else if (!isMobile.value && hidden.value && y.value >= scrollOffset) {
      hidden.value = false;
      scrollbar.value.classList.remove("d-none");
    }
  });

  return scrollbar;
}

/**
 *
 */
function useDropdowns() {
  const dropdownTriggerRefs = ref<Record<string, HTMLElement>>({});
  const dropdownTargetRefs = ref<Record<string, HTMLElement>>({});
  const dropdownInstances = ref<Record<string, DropdownInstance>>({});

  const { y } = useWindowScroll();

  const setupDropdowns = () => {
    Object.keys(dropdownTriggerRefs.value).forEach((id) => {
      const trigger = dropdownTriggerRefs.value[id];
      const target = dropdownTargetRefs.value[id];

      if (!trigger || !target) {
        return;
      }

      dropdownInstances.value[id] = useDropdown(toRef(trigger), toRef(target));
    });
  };

  // Hide dropdowns on scroll.
  watch(y, () => {
    Object.values(dropdownInstances.value).forEach((dropdown) =>
      dropdown.setVisible(false)
    );
  });

  onMounted(() => {
    setupDropdowns();
  });

  return {
    dropdownTriggerRefs,
    dropdownTargetRefs,
    dropdownInstances,
  };
}
